import Button from '@material-ui/core/Button'
import {RelativeLink} from 'lib/ui/link/RelativeLink'
import {useObvioUser} from 'obvio/auth'
import {
  CreditCardRequiredOverlay,
  HasUnpaidTransactionsOverlay,
  Overlay,
  OwnerPastDuePaymentRequiredOverlay,
  PastDuePaymentRequiredOverlay,
  SubscriptionRequiredOverlay,
} from 'obvio/BillingStatusOverlay'
import {obvioRoutes} from 'obvio/Routes'
import {useOrganization} from 'organization/OrganizationProvider'
import {useIsOwner, useOwner} from 'organization/OwnerProvider'
import {
  PURCHASE_ADD_ONS,
  usePermissions,
} from 'organization/PermissionsProvider'
import React from 'react'
import {
  isDelinquent,
  useGetDelinquentSubscription,
} from 'obvio/Billing/subscribe'
import {TeamMember} from 'auth/user'

export function OrganizationBillingStatusOverlay() {
  const {owner} = useOwner()
  const user = useObvioUser()
  const isOwner = useIsOwner()
  const delinquentSubscription = useGetDelinquentSubscription()

  // Skip for e2e test user
  if (user.email === 'test@obv.io') {
    return null
  }

  if (user.has_unpaid_transactions) {
    return <HasUnpaidTransactionsOverlay />
  }

  if (isOwner && delinquentSubscription) {
    return <PastDuePaymentRequiredOverlay />
  }

  if (isOwner && !user.has_active_subscription) {
    return <SubscriptionRequiredOverlay />
  }

  if (
    isOwner &&
    user.has_active_subscription &&
    !user.has_payment_method &&
    !user.is_admin
  ) {
    return <CreditCardRequiredOverlay />
  }

  if (!isOwner && ownerHasDelinquentSubscription(owner)) {
    return <OwnerPastDuePaymentRequiredOverlay />
  }

  if (!owner.has_active_subscription) {
    return <OwnerRequiresSubscriptionOverlay />
  }

  if (owner.has_unpaid_transactions) {
    return <OwnerHasUnpaidTransactionsOverlay />
  }

  if (!owner.has_payment_method && !owner.is_admin) {
    return <OwnerCreditCardRequiredOverlay />
  }

  return null
}

function ownerHasDelinquentSubscription(owner: TeamMember) {
  return owner.subscriptions?.some(isDelinquent) ?? false
}

function OwnerRequiresSubscriptionOverlay() {
  return (
    <Overlay
      title="Inactive Subscription"
      description="The owner of this event does not have an active subscription."
    >
      <BackToOrganizationsButton />
    </Overlay>
  )
}

function OwnerCreditCardRequiredOverlay() {
  return (
    <Overlay
      title="Credit Card Required"
      description="The owner of this event needs to add a credit card to their account."
    >
      <BackToOrganizationsButton />
    </Overlay>
  )
}

function OwnerHasUnpaidTransactionsOverlay() {
  const {can} = usePermissions()
  const {routes} = useOrganization()

  if (can(PURCHASE_ADD_ONS)) {
    return (
      <Overlay
        title="Unpaid Transactions"
        description="The owner of this organization has an incomplete billing transaction."
      >
        <RelativeLink to={routes.add_ons.root} disableStyles>
          <Button variant="contained" color="primary">
            View Transactions
          </Button>
        </RelativeLink>
      </Overlay>
    )
  }

  return (
    <Overlay
      title="Unpaid Transactions"
      description="The owner of this organization has an incomplete billing transaction."
    >
      <BackToOrganizationsButton />
    </Overlay>
  )
}

function BackToOrganizationsButton() {
  return (
    <RelativeLink to={obvioRoutes.organizations.root} disableStyles>
      <Button variant="contained" color="primary">
        Return to My Organizations
      </Button>
    </RelativeLink>
  )
}
